import { useMemo, useRef, useState } from 'react'
import type { ResidentUploadedDocumentRow } from '@/features/resident/utils/residentDocuments'
import { UploadedDocumentFileNameHeader } from './UploadedDocumentFileNameHeader'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { getDocumentStatusTheme } from '../../utils/statusMaps'
import {
  ReimbursementDocumentUploadStatusBadge,
  getReimbursementDocumentUploadStatusTopBarClass,
} from './reimbursementDocumentUploadStatusBadge'
import type { ReimbursementsDetailFileFieldProps } from '../../types/pages'
import { Loader2, Trash2 } from 'lucide-react'
import { attachmentIdsMatch } from '@/features/resident/utils/residentDocuments'
import { DocumentReviewMetadata } from '../modals/DocumentReviewMetadata'
import {
  canRemoveUploadedDocumentAfterReview,
  getDocumentReviewMetadataFromUploadRow,
} from '../../utils/reimbursementDocumentBridge'

export function ReimbursementDetailFileField({
  label,
  accept,
  disabled,
  uploading,
  multiple,
  status,
  documentStatusBadgeVariant = 'default',
  headerExtra,
  footerExtra,
  file,
  files,
  helperText,
  onFileChange,
  onMultiFileChange,
  uploadedRows,
  onDownloadUploaded,
  canRemoveUploaded,
  removingUploadedAttachmentId = null,
  onRequestRemoveUploaded,
}: ReimbursementsDetailFileFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [pendingLargeFile, setPendingLargeFile] = useState<{
    fileList: File[]
    message: string
  } | null>(null)

  const selectedLabel = useMemo(() => {
    if (file) return file.name
    if (files && files.length > 0) {
      return files.length > 1 ? `${files.length} files selected` : files[0]?.name ?? ''
    }
    return ''
  }, [file, files])
  const showText = selectedLabel.trim() || 'Choose file...'
  const hasSelectedFile = Boolean(file || (files && files.length > 0))
  const isUploading = Boolean(uploading && hasSelectedFile)

  const statusText = String(status ?? '').trim()
  const useRequestUploadBadge = documentStatusBadgeVariant === 'requestUpload'
  const statusDisplay = statusText.toUpperCase()
  const theme = getDocumentStatusTheme(statusText)
  const topBarClass = useRequestUploadBadge
    ? getReimbursementDocumentUploadStatusTopBarClass(statusText || 'Pending')
    : theme.topBar
  const labelText = typeof label === 'string' ? label : ''
  const uploadedCount = uploadedRows?.length ?? 0

  const openPicker = () => {
    if (!disabled && !isUploading) inputRef.current?.click()
  }

  const handleFilesSelected = (selectedFiles: File[]) => {
    if (!selectedFiles.length) return
    let hasOver10Mb = false
    const tenMb = 10 * 1024 * 1024
    for (let i = 0; i < selectedFiles.length; i++) {
      if (selectedFiles[i].size > tenMb) {
        hasOver10Mb = true
        break
      }
    }
    if (hasOver10Mb) {
      const fieldNameLower = String(labelText).toLowerCase()
      const isLease = fieldNameLower.includes('lease')
      const confirmMessage = isLease
        ? 'WARNING: Your file is larger than 10MB.\n' +
          'We only need the first 2-3 pages of the lease that shows the named parties, start / end dates, and rent amount.\n' +
          'We request you try to compress the file so it takes less storage.\n' +
          'However, if you are not able to compress it further, please continue.\n' +
          'Do you want to continue?'
        : 'WARNING: Your file is larger than 10MB.\n' +
          'We request you try to compress the file so it takes less storage.\n' +
          'However, if you are not able to compress it further, please continue.\n' +
          'Do you want to continue?'
      setPendingLargeFile({
        fileList: selectedFiles,
        message: confirmMessage,
      })
      if (inputRef.current) inputRef.current.value = ''
      return
    }
    commitSelectedFiles(selectedFiles)
  }

  const commitSelectedFiles = (selectedFiles: File[]) => {
    if (multiple) {
      onMultiFileChange?.(selectedFiles)
    } else {
      onFileChange?.(selectedFiles[0] ?? null)
    }
  }

  const handleConfirmLargeFile = () => {
    if (!pendingLargeFile) return
    const fileList = pendingLargeFile.fileList
    setPendingLargeFile(null)
    commitSelectedFiles(fileList)
  }

  const handleCancelLargeFile = () => {
    if (inputRef.current) inputRef.current.value = ''
    setPendingLargeFile(null)
    if (multiple) {
      onMultiFileChange?.([])
    } else {
      onFileChange?.(null)
    }
  }

  return (
    <div
      className={`flex min-w-0 flex-col gap-3 rounded-xl border border-slate-200 border-t-4 ${topBarClass} bg-white p-4 shadow-sm`}
    >
      <div className="flex items-start justify-between gap-2">
        {label ? (
          typeof label === 'string' ? (
            <span className="block text-sm font-semibold text-slate-700">{label}</span>
          ) : (
            label
          )
        ) : (
          <span />
        )}
        <div className="flex shrink-0 items-center gap-2">
          {statusText ? (
            useRequestUploadBadge ? (
              <ReimbursementDocumentUploadStatusBadge status={statusText} />
            ) : (
              <span className={theme.badge}>{statusDisplay}</span>
            )
          ) : null}
          {headerExtra}
        </div>
      </div>
      <div className="flex min-h-[2.75rem] items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 p-1.5">
        <button
          type="button"
          disabled={disabled || isUploading}
          onClick={openPicker}
          className={`min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm font-normal transition ${
            selectedLabel.trim() ? 'text-slate-700' : 'text-slate-400'
          } hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {showText}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled || isUploading}
          multiple={multiple}
          className="sr-only"
          onChange={(e) => {
            const filesList = e.target.files ? Array.from(e.target.files) : []
            handleFilesSelected(filesList)
          }}
        />
        <button
          type="button"
          disabled={disabled || isUploading}
          onClick={openPicker}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-md bg-[rgb(21_151_255)] px-5 py-1.5 text-sm font-semibold text-white transition hover:bg-[rgb(13_122_204)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin text-white" />
              <span>Uploading…</span>
            </>
          ) : (
            'Upload'
          )}
        </button>
      </div>
      {uploadedCount > 0 ? (
        <div className="w-full">
          <p className="mb-2 text-sm font-semibold text-slate-700">
            Uploaded documents ({uploadedCount})
          </p>
          <div className="space-y-2">
            {uploadedRows!.map((row, index) => {
              const key = String(row.attachment_id ?? `${labelText}-${index}`)
              const reviewMetadata = getDocumentReviewMetadataFromUploadRow(
                row as ResidentUploadedDocumentRow,
              )
              const removeId = row.attachment_id
              return (
                <div
                  key={key}
                  className="flex w-full items-center justify-between gap-3 rounded-lg border border-green-300 bg-green-50 p-2"
                >
                  <div className="min-w-0 flex-1">
                    <UploadedDocumentFileNameHeader
                      fileName={String(row.filename ?? labelText)}
                      onActivate={() => onDownloadUploaded?.(row)}
                      activateDisabled={
                        !onDownloadUploaded ||
                        !(row.filepath || row.file_path || row.attachment_path || row.url || row.path)
                      }
                    />
                    <DocumentReviewMetadata {...reviewMetadata} />
                  </div>
                  {canRemoveUploadedDocumentAfterReview({
                    allowRemove: Boolean(canRemoveUploaded),
                    status: reviewMetadata.status,
                    action: row.action,
                  }) &&
                  removeId !== undefined &&
                  removeId !== null &&
                  onRequestRemoveUploaded ? (
                    <button
                      type="button"
                      className="rounded-lg p-1.5 text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={attachmentIdsMatch(removeId, removingUploadedAttachmentId)}
                      aria-busy={attachmentIdsMatch(removeId, removingUploadedAttachmentId)}
                      onClick={() => onRequestRemoveUploaded(removeId, row.filename ?? labelText)}
                      title={
                        attachmentIdsMatch(removeId, removingUploadedAttachmentId)
                          ? 'Removing file…'
                          : 'Delete file'
                      }
                    >
                      {attachmentIdsMatch(removeId, removingUploadedAttachmentId) ? (
                        <Loader2 size={15} className="animate-spin shrink-0" aria-hidden />
                      ) : (
                        <Trash2 size={15} />
                      )}
                    </button>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
      {footerExtra}
      {helperText ? <p className="text-xs text-slate-500">{helperText}</p> : null}

      <ConfirmDialog
        isOpen={pendingLargeFile != null}
        title="Large File"
        message={pendingLargeFile?.message ?? ''}
        primaryLabel="Yes"
        onPrimary={handleConfirmLargeFile}
        secondaryLabel="No"
        onSecondary={handleCancelLargeFile}
        onDismiss={handleCancelLargeFile}
        messageLayout="largeFile"
        primaryFirst
        actionStyle="application"
        sectionedChrome
      />
    </div>
  )
}
